export function paymentAmountIsActive(amount) {
	return parseFloat(amount) > 0.00001;
}

export function paymentMethodSlug(recordOrMethod) {
	if (recordOrMethod && typeof recordOrMethod === "object") {
		return String(recordOrMethod.payment_method || "").toLowerCase().trim();
	}
	return String(recordOrMethod || "").toLowerCase().trim();
}

export function isProcessorRefundMethod(method) {
	const slug = paymentMethodSlug(method);
	return slug === "card" || slug === "terminal" || slug === "debit";
}

/**
 * Processor-backed tenders: credit, terminal, or debit (debit still uses processor_amount).
 */
export function isProcessorTender(record) {
	if (isProcessorRefundMethod(record) || record?.is_debit) {
		return true;
	}
	return paymentAmountIsActive(record?.processor_amount);
}

export function isZelleRefundMethod(method) {
	return paymentMethodSlug(method) === "zelle";
}

export function isZelleTender(record) {
	return isZelleRefundMethod(record) || paymentAmountIsActive(record?.zelle_amount);
}

/**
 * Default disbursement for a merchandise refund. Debit goes back through the processor
 * (Card). Mixed always defaults to Cash so the processor is not over-refunded.
 * Zelle/check/gift/invoice default to Other/Zelle so they do not hit the drawer.
 */
export function defaultTransactionRefundMethod(tx) {
	const method = paymentMethodSlug(tx);
	if (method === "mixed") {
		return "cash";
	}
	if (isProcessorTender(tx)) {
		return "card";
	}
	if (isZelleTender(tx) || isZelleRefundMethod(method)) {
		return "zelle";
	}
	if (method === "cash" || method === "") {
		return "cash";
	}
	return "other";
}

const PAYMENT_METHOD_SLUG_LABELS = {
	cash: "Cash",
	check: "Check",
	zelle: "Zelle",
	card: "Card",
	terminal: "Terminal",
	debit: "Debit",
	gift: "Gift",
	invoice: "Invoice",
	other: "Other",
	mixed: "Mixed",
	adjustment: "Adjustment",
};

export function formatPaymentMethodSlugLabel(method) {
	const slug = paymentMethodSlug(method);
	if (!slug) {
		return "";
	}
	if (PAYMENT_METHOD_SLUG_LABELS[slug]) {
		return PAYMENT_METHOD_SLUG_LABELS[slug];
	}
	return slug.charAt(0).toUpperCase() + slug.slice(1);
}

/**
 * Derive a display label from payment amount columns (mirrors backend Utilities helpers).
 *
 * @param {object} record
 * @returns {string}
 */
export function derivePaymentMethodLabel(record) {
	const labels = [];

	if (paymentAmountIsActive(record.cash_amount)) {
		labels.push('Cash');
	}
	if (paymentAmountIsActive(record.check_amount)) {
		labels.push('Check');
	}
	if (paymentAmountIsActive(record.zelle_amount)) {
		labels.push('Zelle');
	}
	if (paymentAmountIsActive(record.invoice_amount)) {
		labels.push('Invoice');
	}
	if (paymentAmountIsActive(record.other_amount)) {
		labels.push('Gift');
	}
	if (paymentAmountIsActive(record.processor_amount)) {
		if (record.is_debit || paymentMethodSlug(record) === 'debit') {
			labels.push('Debit');
		} else {
			labels.push(paymentMethodSlug(record) === 'terminal' ? 'Terminal' : 'Card');
		}
	}

	if (labels.length === 0) {
		return formatPaymentMethodSlugLabel(record);
	}
	if (labels.length === 1) {
		return labels[0];
	}
	if (labels.length === 2) {
		return labels[0] + '/' + labels[1];
	}
	return 'Mixed';
}
